/**
 * Crédits (forfaits Mindbody, GET client/services) de Mon compte v2 — SPEC MC3, VC4 (retour M4).
 *
 * Mêmes règles d'appartenance que la v1, seul l'ordre change :
 * - Mon compte (= UserProfile.tsx Credits()) : forfaits non expirés ET avec au moins une séance ;
 * - « Voir tous mes crédits » (= Services/ServicesHistory.tsx) : actifs = non expirés (fin de journée
 *   d'expiration incluse), expirés = le reste.
 * Tri : actifs par date d'expiration CROISSANTE (le plus proche d'abord, M4) ; expirés par date
 * d'expiration décroissante (SPEC VC4, proposition). La v1 triait par date d'activation décroissante.
 */
import type { ClientService } from 'types/client';
import { daysUntil, toDate } from './format';

/** Alerte « expire bientôt » (SPEC MC3, proposition : valeur de la maquette). */
export const EXPIRING_SOON_DAYS = 14;

/** Fin de la journée locale d'une date (un forfait qui expire aujourd'hui reste utilisable aujourd'hui). */
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

const time = (value: string | null | undefined) => toDate(value)?.getTime() ?? Number.POSITIVE_INFINITY;

/** Forfait non expiré à `now` (même règle que ServicesHistory : endOfDay(expiration) >= maintenant). */
export function isServiceActive(service: ClientService, now: Date = new Date()): boolean {
  const expiration = toDate(service.ExpirationDate);
  if (!expiration) return false;
  return endOfDay(expiration).getTime() >= now.getTime();
}

/** Expiration la plus proche d'abord ; à égalité, activation la plus récente d'abord (ordre v1). */
export function byExpirationAsc(a: ClientService, b: ClientService): number {
  const diff = time(a.ExpirationDate) - time(b.ExpirationDate);
  if (diff !== 0) return diff;
  return time(b.ActiveDate) - time(a.ActiveDate);
}

/** Crédits actifs de Mon compte : non expirés, au moins une séance restante, expiration croissante. */
export function accountWallets(services: ClientService[] | Record<string, ClientService> | undefined, now = new Date()) {
  const list = services ? Object.values(services) : [];
  return list.filter((s) => isServiceActive(s, now) && s.Remaining > 0).sort(byExpirationAsc);
}

/** « Voir tous mes crédits » : actifs (expiration croissante) et expirés (expiration décroissante). */
export function splitServices(services: ClientService[] | Record<string, ClientService> | undefined, now = new Date()) {
  const list = services ? Object.values(services) : [];
  const active = list.filter((s) => isServiceActive(s, now)).sort(byExpirationAsc);
  const expired = list.filter((s) => !isServiceActive(s, now)).sort((a, b) => -byExpirationAsc(a, b));
  return { active, expired };
}

/** Forfait qui expire dans 14 jours ou moins. */
export function isExpiringSoon(service: ClientService, now: Date = new Date()): boolean {
  const expiration = toDate(service.ExpirationDate);
  return !!expiration && daysUntil(expiration, now) <= EXPIRING_SOON_DAYS;
}

/** Part restante de la jauge, 0..100. */
export function walletPercent(service: Pick<ClientService, 'Remaining' | 'Count'>): number {
  if (!service.Count || service.Count <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((service.Remaining / service.Count) * 100)));
}

export type WalletStatus = 'active' | 'soon' | 'empty' | 'expired';

/** Statut d'un forfait dans le tableau « Voir tous mes crédits ». */
export function walletStatus(service: ClientService, now: Date = new Date()): WalletStatus {
  if (!isServiceActive(service, now)) return 'expired';
  if (service.Remaining <= 0) return 'empty';
  return isExpiringSoon(service, now) ? 'soon' : 'active';
}

/** Total des séances restantes sur les forfaits actifs. */
export function remainingTotal(services: ClientService[]): number {
  return services.reduce((sum, s) => sum + Math.max(0, s.Remaining || 0), 0);
}
